import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import Alert from "@mui/material/Alert";
import OtpVerificationView from "./OtpVerificationView";
import { HttpStatusCode } from "axios";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import PasswordStrengthBar from "react-password-strength-bar";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import { useTheme } from "@mui/material/styles";
import Link from "@mui/material/Link";
import { axiosHttp } from "../../AxiosWrapper";
import { useSignInSignUp } from "../../context/SignInSignUpProvider";
import { isValidEmail } from "../../utils/CommonUtil";

const ForgotPasswordView = (props) => {
    const [data, setData] = useState({ state: "INITIATED" });
    const [errorMap, setErrorMap] = useState({});
    const [inProgress, setInProgress] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { singnIn } = useSignInSignUp();

    const theme = useTheme();

    const handleInputchange = (e) => {
        const { name, value } = e.target;
        setData({ ...data, [name]: value });
    }

    const submitEmail = () => {
        let errors = {}
        if (!data.email || data.email.trim() === "") {
            errors.email = "Please enter an email address!"
        } else if (!isValidEmail(data.email)) {
            errors.email = "Please enter a valid email address!"
        }
        setErrorMap(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }
        setInProgress(true);
        axiosHttp.post("/api/pub/dss/v1/forgotPassword", { email: data.email })
            .then((response) => {
                if (response.data.status === HttpStatusCode.Ok) {
                    setData({ ...data, state: "VERIFY_OTP", token: response.data.content.token });
                } else if(response.data.status === HttpStatusCode.NotFound){
                    setData({ ...data, responseError: "NOT_FOUND" });
                }else if(response.data.status === HttpStatusCode.ServiceUnavailable){
                    setData({ ...data, responseError: "SERVICE_UNAVAILABLE" });
                }
            })
            .catch((error) => { })
            .then((res) => {
                setInProgress(false);
            })
    }

    const submitPassword = () => {
        setInProgress(true);
        axiosHttp.post("/api/pub/dss/v1/updatePassword", { password: data.password, confirmPassword: data.confirmPassword, token: data.otpVerifyToken })
            .then((response) => {
                if (response.data.status === HttpStatusCode.Ok) {
                    setData({ ...data, state: "UPDATE_PWD_SUCCESS" });
                } else {
                    setData({ ...data, state: "UPDATE_PWD_FAILED" });
                }
            })
            .catch((error) => { })
            .then((res) => {
                setInProgress(false);
            })
    }

    const handleOtpVerified = (response) => {
        if (response.status === HttpStatusCode.Ok) {
            setData({ ...data, state: "RESET_PASSWORD", otpVerifyToken: response.content.otpVerifyToken, responseError: null })
        } else {
            setData({ ...data, responseError: "OPT_VERIFICATION_FAILED" });
        }
    }

    const styles = {
        label: {
            fontWeight: 600,
            mb: .5,
            textAlign: "start"
        },
    }

    return (
        <Box>
            {data.state === "INITIATED" &&
                <Stack spacing={2} justifyContent="center" sx={{
                    px: 4,
                    py: 4
                }} className="animate-fade-in">
                    <Typography sx={{ fontWeight: 500 }}>Enter your email address you'd like the password reset OTP sent to</Typography>
                    <TextField
                        error={errorMap.email}
                        helperText={errorMap.email}
                        name="email"
                        placeholder="Email"
                        onChange={handleInputchange}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end"><EmailIcon color="primary" /> </InputAdornment>
                                )
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                submitEmail();
                            }
                        }}
                    />
                    <Box sx={{ display: "flex", pt: 1, }}>
                        <Button sx={{
                            py: 1.5,
                            borderRadius: 6,
                            width: { xs: "100%", md: "50%" },

                            margin: "auto"
                        }} onClick={submitEmail} loading={inProgress} variant="contained">Continue</Button>
                    </Box>
                    {data.responseError === "NOT_FOUND" &&
                        <Alert sx={{ mt: 1, borderRadius: 3, justifyContent: "center" }} severity="error">
                            <Typography>Could not find any accound associated with the email address!</Typography>
                        </Alert>
                    }

                    {data.responseError === "SERVICE_UNAVAILABLE" &&
                        <Alert sx={{ mt: 1, borderRadius: 3, justifyContent: "center" }} severity="warning">
                            <Typography>Something went wrong!. Please try again later.</Typography>
                        </Alert>
                    }

                </Stack>
            }
            {data.state === "VERIFY_OTP" &&
                <Box sx={{
                    px: 2.5,
                    py: 4,
                    textAlign: "center"
                }} className="animate-fade-in">
                    <Typography sx={{ mt: 1, fontWeight: 500 }}> Please enter the OTP sent to your email.</Typography>
                    <Typography sx={{ opacity: .6 }}>{data.email}</Typography>
                    <OtpVerificationView ref="forgot-password" token={data.token} verifyCallback={handleOtpVerified} />

                    {data.responseError === "OPT_VERIFICATION_FAILED" &&
                        <Alert sx={{ mt: 1, borderRadius: 3, justifyContent: "center" }} severity="error">
                            <Typography>Incorrect OTP!. Please check the OTP received.</Typography>
                            <Typography>or</Typography>
                            <Typography>Click Resend OTP and try again.</Typography>
                        </Alert>
                    }
                </Box>
            }
            {data.state === "RESET_PASSWORD" &&
                <Box sx={{
                    px: 4,
                    py: 4,
                    textAlign: "center"
                }} className="animate-fade-in">

                    <Stack spacing={2}>
                        <Box>
                            <Typography sx={styles.label}>Password</Typography>
                            <TextField fullWidth sx={styles.text}
                                type={showPassword ? 'text' : 'password'}
                                onChange={handleInputchange}
                                name="password"
                                value={data.password}
                                error={errorMap['password']}
                                helperText={errorMap['password']}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onMouseUp={(e) => e.preventDefault()}
                                                    edge="end"
                                                    color="primary"
                                                >
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                            {data.password &&
                                <Box sx={{ mt: 1.5, px: .5 }}>
                                    <PasswordStrengthBar
                                        password={data.password}
                                        shortScoreWord="Very short"
                                        scoreWords={["very short", "weak", "medium", "strong", "very strong"]}
                                        onChangeScore={(score) => setData({ ...data, passwordScore: score })}
                                    />
                                </Box>
                            }
                        </Box>

                        <Box>
                            <Typography sx={styles.label}>Confirm Password</Typography>
                            <TextField fullWidth sx={styles.text}
                                type={showConfirmPassword ? 'text' : 'password'}
                                onChange={handleInputchange}
                                name="confirmPassword"
                                value={data.confirmPassword}
                                error={errorMap['confirmPassword']}
                                helperText={errorMap['confirmPassword']}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onMouseUp={(e) => e.preventDefault()}
                                                    edge="end"
                                                    color="primary"
                                                >
                                                    {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }}

                            />
                        </Box>
                        <Box sx={{ mt: 4 }}>
                            <Button onClick={submitPassword} sx={{ px: 5, py: 1.5, borderRadius: 6 }} variant="contained">Change Password</Button>
                        </Box>
                    </Stack>
                </Box>
            }

            {data.state === "UPDATE_PWD_SUCCESS" &&
                <Box sx={{
                    px: 2.5,
                    py: 4,
                    textAlign: "center"
                }} className="animate-fade-in">
                    <CheckIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>Your password has been updated successfully.</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>You can now {<Link component="button" onClick={singnIn} sx={{ textDecoration: "none" }}>Sign in</Link>} with your new password.</Typography>
                </Box>
            }

            {data.state === "UPDATE_PWD_FAILED" &&
                <Box sx={{
                    px: 2.5,
                    pb: 2,
                    textAlign: "center"
                }} className="animate-fade-in">
                    <Alert severity="error">Update password failed!. Please try again.</Alert>
                    <Button sx={{ mt: 2 }} onClick={props.onClose}>Close</Button>

                </Box>
            }

        </Box>
    );
};

export default ForgotPasswordView;